import { ArrowDown, ArrowUp, ChevronRight, Info } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SegmentButton } from '@/components/ui/segment-button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FEED_STATS_GROUPINGS,
  type FeedStatsGrouping,
  type FeedStatsRow,
} from '@/features/action-card-feed/action-card-feed-types';
import { useActionCardFeedStats } from '@/features/action-card-feed/useActionCardFeedStats';
import {
  interval95,
  SMALL_SAMPLE,
} from '@/features/online-eval/agent-rate-stats';
import { useDebouncedValue } from '@/features/proactivity-experiments/useDebouncedValue';
import { useTheme } from '@/hooks/useTheme';
import type { ThemeMode } from '@/lib/theme';

type FormatGroup = (group: FeedStatsRow['group']) => string;

const GROUPINGS: Record<
  FeedStatsGrouping,
  { label: string; hint: string; format: FormatGroup }
> = {
  action_type: {
    label: 'Card type',
    hint: 'The kind of card, such as an email draft.',
    format: String,
  },
  importance: {
    label: 'Importance',
    hint: 'The importance the agent gave the card. Not rated when it gave none.',
    format: (group) => (group === null ? 'Not rated' : String(group)),
  },
  made_by: {
    label: 'Made by',
    hint: "The agent whose turn made the card, or the card's origin when no agent did.",
    format: String,
  },
  routine: {
    label: 'Routine',
    hint: "The routine whose run made the card. A title the routines of three or more users share shows by name. The rest go under Other routine we made or User's own routine.",
    format: String,
  },
  seen_for: {
    label: 'Seen for',
    hint: "The card's time on screen in a focused app window, added up over all its showings. It says the card was visible, not that the user read it. No time recorded means the row never stayed on screen in a focused window for 15 s, or the app closed before its first report.",
    format: String,
  },
  week: {
    label: 'Week',
    hint: 'The Monday of the week the card was shown, in UTC. A card shown in two weeks counts in both.',
    format: String,
  },
  position: {
    label: 'Position',
    hint: "The card's row in the list. 1 is the top. The newest card is on top, so the row also tells the card's age.",
    // The server counts rows from 0 at the top of the list
    format: (group) => String(Number(group) + 1),
  },
  list_size: {
    label: 'List size',
    hint: 'How many cards the list held.',
    format: String,
  },
  surface: {
    label: 'List',
    hint: 'Home or the Feed.',
    format: (group) => (group === 'home' ? 'Home' : 'Feed'),
  },
};

const GROUPING_OPTIONS = FEED_STATS_GROUPINGS.map((value) => ({
  value,
  label: GROUPINGS[value].label,
  title: GROUPINGS[value].hint,
}));

// The UTC day as the date inputs and the API write it
const utcDay = (daysAgo: number): string =>
  new Date(Date.now() - daysAgo * 86_400_000).toISOString().slice(0, 10);

// Today and the days before it
const DAY_WINDOWS = [7, 30, 90].map((days) => ({
  value: days,
  label: `${days} days`,
}));

const lastDays = (days: number) => ({
  start: utcDay(days - 1),
  end: utcDay(0),
});

const shareOf = (count: number, cards: number): number => (100 * count) / cards;

const neither = (row: FeedStatsRow): number =>
  row.cards - row.acted_on - row.run_failed - row.dismissed;

// Acted on and dismissed sit at the two ends of each bar, so both read off an edge
const OUTCOMES: {
  label: string;
  count: (row: FeedStatsRow) => number;
  color: Record<ThemeMode, string>;
}[] = [
  {
    label: 'Acted on',
    count: (row) => row.acted_on,
    color: { dark: '#46a689', light: '#3a9371' },
  },
  {
    label: 'Run failed',
    count: (row) => row.run_failed,
    color: { dark: '#d4a03a', light: '#b7791f' },
  },
  {
    label: 'Neither',
    count: neither,
    color: { dark: '#475569', light: '#94a3b8' },
  },
  {
    label: 'Dismissed',
    count: (row) => row.dismissed,
    color: { dark: '#b75648', light: '#a63836' },
  },
];

const UNITS_OF_TIME: [string, number][] = [
  ['d', 86_400_000],
  ['h', 3_600_000],
  ['min', 60_000],
  ['s', 1_000],
];

// In the largest unit the time fills, so 90 seconds reads as 1.5 min
const duration = (ms: number): string => {
  const [unit, unitMs] =
    UNITS_OF_TIME.find(([, size]) => ms >= size) ?? UNITS_OF_TIME[3];
  return `${(ms / unitMs).toFixed(1)} ${unit}`;
};

// The bands the server counts in: SEEN_MS_BAND_EDGES and OPEN_BAND_EDGES in action_card_impression_repo.py
const SEEN_BANDS = [
  'under 2 s',
  '2-5 s',
  '5-15 s',
  '15-60 s',
  '1-5 min',
  'over 5 min',
];
const OPEN_BANDS = [
  'under 1 h',
  '1-6 h',
  '6 h-1 d',
  '1-3 d',
  '3-7 d',
  'over 7 d',
];

// One row's counts in the bands of one column, what they count, and the words for each band's count
type BandCounts = {
  bands: string[];
  unit: string;
  solid: number[];
  outline?: number[];
  describe: (band: number) => string;
};

const seenCounts = (row: FeedStatsRow): BandCounts => ({
  bands: SEEN_BANDS,
  unit: 'showings with a time on screen',
  solid: row.seen_ms_by_band,
  describe: (band) => `${row.seen_ms_by_band[band]} showings`,
});

const openCounts = (row: FeedStatsRow): BandCounts => ({
  bands: OPEN_BANDS,
  unit: 'cards',
  solid: row.closed_by_band,
  outline: row.still_open_by_band,
  describe: (band) =>
    `${row.closed_by_band[band]} closed, ${row.still_open_by_band[band]} still open`,
});

const totalsOf = ({ solid, outline }: BandCounts): number[] =>
  solid.map((count, band) => count + (outline?.[band] ?? 0));

// One band's bar. The outlined part sits on the solid one, and an empty band is a line
const Bar = ({
  solid,
  outline,
  height,
  className,
}: {
  solid: number;
  outline: number;
  height: string;
  className: string;
}) =>
  solid + outline === 0 ? (
    <div className={`h-px bg-border ${className}`} />
  ) : (
    <div className={`flex min-h-0.5 flex-col ${className}`} style={{ height }}>
      {outline ? (
        <div
          className="border border-muted-foreground"
          style={{ flexGrow: outline }}
        />
      ) : null}
      {solid ? (
        <div className="bg-muted-foreground" style={{ flexGrow: solid }} />
      ) : null}
    </div>
  );

// The small chart in a cell: one bar per band, as tall as its count against the tallest
const BandHistogram = ({ counts }: { counts: BandCounts }) => {
  const totals = totalsOf(counts);
  const tallest = Math.max(...totals);
  const summary = counts.bands
    .map((label, band) => `${label}: ${counts.describe(band)}`)
    .join('; ');
  return (
    <div
      role="img"
      aria-label={summary}
      title={summary}
      className="flex h-5 items-end gap-0.5"
    >
      {counts.bands.map((label, band) => (
        <Bar
          key={label}
          solid={counts.solid[band]}
          outline={counts.outline?.[band] ?? 0}
          height={`${(100 * totals[band]) / tallest}%`}
          className="w-2.5"
        />
      ))}
    </div>
  );
};

// A small band shows as <1%, so a band with cards in it never reads as 0%
const percent = (share: number): string =>
  share > 0 && share < 1 ? '<1%' : `${Math.round(share)}%`;

const AXIS_STEP = 25;

// The axis of one chart: a grid line and a label every 25%, up to the step over the largest share
const BandAxis = ({ top }: { top: number }) => {
  const ticks = Array.from(
    { length: top / AXIS_STEP + 1 },
    (_, step) => step * AXIS_STEP
  );
  return (
    <>
      <div className="relative w-8 shrink-0 text-[10px] tabular-nums text-muted-foreground">
        {ticks.map((tick) => (
          <span
            key={tick}
            className="absolute right-0 translate-y-1/2"
            style={{ bottom: `${(100 * tick) / top}%` }}
          >
            {tick}%
          </span>
        ))}
      </div>
      {ticks.map((tick) => (
        <div
          key={tick}
          className="absolute inset-x-0 border-t border-border/60"
          style={{ bottom: `${(100 * tick) / top}%`, left: '2.5rem' }}
        />
      ))}
    </>
  );
};

// The chart under an opened row: each band's share of the row's showings or cards, over the band's name
const BandChart = ({
  title,
  counts,
  median,
  note,
}: {
  title: string;
  counts: BandCounts;
  median: number | null;
  note?: string;
}) => {
  const totals = totalsOf(counts);
  const total = totals.reduce((sum, count) => sum + count, 0);
  const shares = totals.map((count) => (100 * count) / total);
  const top = Math.ceil(Math.max(...shares) / AXIS_STEP) * AXIS_STEP;
  return (
    <div className="w-full max-w-md">
      <div className="text-sm font-medium text-foreground">{title}</div>
      <div className="text-xs text-muted-foreground">
        {total === 0
          ? `No ${counts.unit}`
          : `Share of ${total.toLocaleString()} ${counts.unit}`}
        {median === null ? '' : `, median ${duration(median)}`}
      </div>
      {total === 0 ? null : (
        <>
          <div className="relative mt-6 flex h-28 gap-2">
            <BandAxis top={top} />
            <div className="relative grid flex-1 grid-cols-6 gap-3">
              {counts.bands.map((label, band) => (
                <div
                  key={label}
                  title={`${label}: ${counts.describe(band)}`}
                  className="flex flex-col items-center justify-end gap-1"
                >
                  <span className="text-xs tabular-nums">
                    {percent(shares[band])}
                  </span>
                  <Bar
                    solid={counts.solid[band]}
                    outline={counts.outline?.[band] ?? 0}
                    height={`${(100 * shares[band]) / top}%`}
                    className="w-full max-w-10 shrink-0"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="ml-10 mt-1.5 grid grid-cols-6 gap-3">
            {counts.bands.map((label) => (
              <span
                key={label}
                className="whitespace-nowrap text-center text-xs text-muted-foreground"
              >
                {label}
              </span>
            ))}
          </div>
        </>
      )}
      {note ? (
        <div className="ml-10 mt-2 text-xs text-muted-foreground">{note}</div>
      ) : null}
    </div>
  );
};

const RowCharts = ({ row }: { row: FeedStatsRow }) => (
  <div className="flex flex-wrap gap-x-12 gap-y-6 px-2 py-4">
    <BandChart
      title="Time per showing"
      counts={seenCounts(row)}
      median={row.median_seen_ms}
    />
    <BandChart
      title="Time open"
      counts={openCounts(row)}
      median={row.median_open_ms}
      note="Solid is closed, outlined is still open"
    />
  </div>
);

// The median with the number of showings it is built on, after the showings counted per band
const timePerShowing = (row: FeedStatsRow): ReactNode =>
  row.median_seen_ms === null ? (
    '-'
  ) : (
    <div className="flex items-center justify-end gap-2.5">
      <BandHistogram counts={seenCounts(row)} />
      <span>
        {duration(row.median_seen_ms)}{' '}
        <span className="text-muted-foreground">
          n = {row.timed_impressions.toLocaleString()}
        </span>
      </span>
    </div>
  );

// The median, after the cards counted per band: solid for closed, outlined for still open
const timeOpen = (row: FeedStatsRow): ReactNode =>
  row.median_open_ms === null ? (
    '-'
  ) : (
    <div className="flex items-center justify-end gap-2.5">
      <BandHistogram counts={openCounts(row)} />
      <span>{duration(row.median_open_ms)}</span>
    </div>
  );

const countWithShare = (count: number, cards: number): ReactNode => (
  <>
    {count.toLocaleString()}{' '}
    <span className="text-muted-foreground">
      {Math.round(shareOf(count, cards))}%
    </span>
  </>
);

// The share acted on of the cards the user acted on or dismissed, null when the user decided on none
const decidedShare = (row: FeedStatsRow): number | null => {
  const decided = row.acted_on + row.dismissed;
  return decided === 0 ? null : row.acted_on / decided;
};

// The share acted on of all the rows' decided cards. A card that counts in two rows counts twice
const decidedShareOfAllRows = (rows: FeedStatsRow[]): number => {
  const actedOn = rows.reduce((sum, row) => sum + row.acted_on, 0);
  const dismissed = rows.reduce((sum, row) => sum + row.dismissed, 0);
  return actedOn / (actedOn + dismissed);
};

// 1 px per percent, set in so that the label over a dot at 0% or 100% stays inside
const PLOT_INSET = 16;
const xOf = (share: number): number => PLOT_INSET + 100 * share;
const PLOT_TICKS = [0, 0.25, 0.5, 0.75, 1];

// The share acted on of the cards the user decided on as a dot, with its 95% interval as a whisker and the share of
// all rows as a dashed line. An open row shows the numbers instead
const ActedOnWhenDecided = ({
  row,
  shareOfAllRows,
  isOpen,
}: {
  row: FeedStatsRow;
  shareOfAllRows: number;
  isOpen: boolean;
}) => {
  const share = decidedShare(row);
  if (share === null) return '-';
  const decided = row.acted_on + row.dismissed;
  const [low, high] = interval95({ k: row.acted_on, n: decided });
  const sharePercent = `${Math.round(100 * share)}%`;
  const interval = `${Math.round(100 * low)}-${Math.round(100 * high)}%`;
  const count = `n = ${decided.toLocaleString()}`;
  const grey = decided < SMALL_SAMPLE ? 'text-muted-foreground' : '';
  if (isOpen) {
    return (
      <span className={`whitespace-nowrap ${grey}`}>
        {sharePercent}{' '}
        <span className="text-muted-foreground">
          {interval} {count}
        </span>
      </span>
    );
  }
  const summary = `${row.acted_on} acted on, ${row.dismissed} dismissed. 95% interval ${interval}. All rows ${Math.round(100 * shareOfAllRows)}%`;
  return (
    <div className={`flex items-center justify-end gap-2.5 ${grey}`}>
      <div role="img" aria-label={summary} title={summary}>
        <svg width={xOf(1) + PLOT_INSET} height={28}>
          {PLOT_TICKS.map((tick) => (
            <line
              key={tick}
              x1={xOf(tick)}
              x2={xOf(tick)}
              y1={13}
              y2={27}
              className="stroke-border"
            />
          ))}
          <line
            x1={xOf(shareOfAllRows)}
            x2={xOf(shareOfAllRows)}
            y1={13}
            y2={27}
            strokeDasharray="2 2"
            className="stroke-muted-foreground"
          />
          <path
            d={`M${xOf(low)} 16v8M${xOf(low)} 20H${xOf(high)}M${xOf(high)} 16v8`}
            strokeWidth={1.5}
            className="fill-none stroke-muted-foreground"
          />
          <circle cx={xOf(share)} cy={20} r={3.5} className="fill-current" />
          <text
            x={xOf(share)}
            y={10}
            textAnchor="middle"
            className="fill-current text-xs"
          >
            {sharePercent}
          </text>
        </svg>
      </div>
      {/* A fixed width keeps the plots of all rows in line */}
      <span className="w-16 whitespace-nowrap text-left text-muted-foreground">
        {count}
      </span>
    </div>
  );
};

const TableLegend = () => {
  const theme = useTheme();
  return (
    <div className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
      <div className="flex gap-4">
        {OUTCOMES.map(({ label, color }) => (
          <span key={label} className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: color[theme] }}
            />
            {label}
          </span>
        ))}
      </div>
      <span className="flex items-center gap-1.5">
        Time open:
        <span className="h-2.5 w-2.5 bg-muted-foreground" />
        closed
        <span className="h-2.5 w-2.5 border border-muted-foreground" />
        still open
      </span>
    </div>
  );
};

const OutcomeBar = ({ row }: { row: FeedStatsRow }) => {
  const theme = useTheme();
  const shares = OUTCOMES.map(
    ({ label, count }) =>
      `${label} ${Math.round(shareOf(count(row), row.cards))}%`
  ).join(', ');
  return (
    <div
      role="img"
      aria-label={shares}
      title={shares}
      className="flex h-2 w-40 gap-px overflow-hidden rounded-full"
    >
      {OUTCOMES.filter(({ count }) => count(row) > 0).map(
        ({ label, count, color }) => (
          <div
            key={label}
            style={{ flexGrow: count(row), backgroundColor: color[theme] }}
          />
        )
      )}
    </div>
  );
};

const OUTCOME_HINT =
  "The group's cards: acted on, neither, dismissed. It sorts by the share acted on.";

type SortBy = (row: FeedStatsRow) => string | number | null;
type Direction = 'ascending' | 'descending';
type Sort = { column: string; sortBy: SortBy; direction: Direction };

const actedOnShare: SortBy = (row) => row.acted_on / row.cards;

// A count sorts by its share of the group's cards
const NUMBER_COLUMNS: { label: string; hint: string; sortBy: SortBy }[] = [
  {
    label: 'Impressions',
    hint: 'Showings of a card on one row for at least 1 s. A card that moves rows has several.',
    sortBy: (row) => row.impressions,
  },
  {
    label: 'Cards',
    hint: 'The different cards shown. A card shown under two groups counts in both.',
    sortBy: (row) => row.cards,
  },
  {
    label: 'Acted on',
    hint: 'The user approved the card and its action succeeded, ticked it off, or marked it done elsewhere.',
    sortBy: actedOnShare,
  },
  {
    label: 'Run failed',
    hint: 'The action ran and failed, from an approval by the user or by the agent, which the card does not tell apart. The card waits for a retry or a dismissal.',
    sortBy: (row) => row.run_failed / row.cards,
  },
  {
    label: 'Dismissed',
    hint: 'The user dismissed the card.',
    sortBy: (row) => row.dismissed / row.cards,
  },
  {
    label: 'Neither',
    hint: 'Still waiting, or closed without the user, for example by the agent.',
    sortBy: (row) => neither(row) / row.cards,
  },
  {
    label: 'Acted on when decided',
    hint: `Of the cards the user acted on or dismissed, the share acted on. The dot is that share and the whisker its 95% interval. The dashed line is the share over all rows, and a row whose whisker crosses it may not differ from the rest. Cards still waiting or closed without the user are left out. Under ${SMALL_SAMPLE} cards it shows grey. Open a row to see the numbers.`,
    sortBy: decidedShare,
  },
  {
    label: 'Time per showing',
    hint: `Time one showing's row was on screen in a focused app window, which says the card was visible, not that the user read it. The bars count showings: ${SEEN_BANDS.join(', ')}. A dash means no showing reported a time. Open a row to see each band's share.`,
    sortBy: (row) => row.median_seen_ms,
  },
  {
    label: 'Time open',
    hint: `From the card's creation to its close by the user or the agent, or to now. The bars count cards: ${OPEN_BANDS.join(', ')}. Solid is closed, outlined is still open. Open a row to see each band's share.`,
    sortBy: (row) => row.median_open_ms,
  },
];

const COLUMN_COUNT = 2 + NUMBER_COLUMNS.length;

// An info icon whose hover text, from the browser, says what a column or grouping means
const Labelled = ({ label, hint }: { label: string; hint: string }) => (
  <span className="inline-flex items-center gap-1">
    {label}
    <span
      role="img"
      aria-label={hint}
      title={hint}
      className="cursor-help text-muted-foreground/70 hover:text-foreground"
    >
      <Info className="h-3.5 w-3.5" />
    </span>
  </span>
);

// Largest first, then smallest first, then the server's order
const nextSort = (
  sort: Sort | null,
  column: string,
  sortBy: SortBy
): Sort | null => {
  if (sort?.column !== column) {
    return { column, sortBy, direction: 'descending' };
  }
  return sort.direction === 'descending'
    ? { ...sort, direction: 'ascending' }
    : null;
};

// A group with no value goes last either way
const sortRows = (
  rows: FeedStatsRow[],
  { sortBy, direction }: Sort
): FeedStatsRow[] =>
  [...rows].sort((a, b) => {
    const [x, y] = [sortBy(a), sortBy(b)];
    if (x === null || y === null) {
      return Number(x === null) - Number(y === null);
    }
    const order =
      typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x).localeCompare(String(y), undefined, { numeric: true });
    return direction === 'descending' ? -order : order;
  });

const SORT_ARROWS = { ascending: ArrowUp, descending: ArrowDown };

const SortHead = ({
  column,
  label,
  hint,
  sortBy,
  sort,
  onSort,
  className,
}: {
  column: string;
  label: string;
  hint: string;
  sortBy: SortBy;
  sort: Sort | null;
  onSort: (sort: Sort | null) => void;
  className?: string;
}) => {
  const direction = sort?.column === column ? sort.direction : undefined;
  const Arrow = direction && SORT_ARROWS[direction];
  return (
    <TableHead aria-sort={direction} className={className}>
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, column, sortBy))}
        className="inline-flex cursor-pointer items-center gap-1 hover:text-foreground"
      >
        <Labelled label={label} hint={hint} />
        {Arrow ? <Arrow className="h-3.5 w-3.5" aria-hidden /> : null}
      </button>
    </TableHead>
  );
};

export const ActionCardFeedPage = () => {
  const [groupBy, setGroupBy] = useState<FeedStatsGrouping>('action_type');
  const [period, setPeriod] = useState(() => lastDays(30));
  const [userSearch, setUserSearch] = useState('');
  const user = useDebouncedValue(userSearch.trim(), 400);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort | null>(null);
  const {
    data: rows = [],
    isLoading,
    isError,
    error,
  } = useActionCardFeedStats(groupBy, { ...period, user });
  const grouping = GROUPINGS[groupBy];
  const shareOfAllRows = decidedShareOfAllRows(rows);
  const shownRows = sort === null ? rows : sortRows(rows, sort);
  // 0 presses no button: the dates are the user's own
  const days =
    DAY_WINDOWS.find(
      ({ value }) =>
        lastDays(value).start === period.start &&
        lastDays(value).end === period.end
    )?.value ?? 0;

  // TODO: drop the border-border classes once the admin app sets a default border color
  return (
    <Card className="border-border [&_tr]:border-border">
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <CardTitle>Action Card Feed</CardTitle>
            <CardDescription>
              The cards that Home and the Feed showed in the chosen period, and
              what users did with them since. Only the desktop and web apps
              report what they show.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-3 [&_.border]:border-border">
            {/* The grouping buttons do not wrap, so a narrow window scrolls them */}
            <div className="max-w-full overflow-x-auto">
              <SegmentButton
                options={GROUPING_OPTIONS}
                selected={groupBy}
                onSelect={setGroupBy}
              />
            </div>
            <SegmentButton
              options={DAY_WINDOWS}
              selected={days}
              onSelect={(value) => setPeriod(lastDays(value))}
            />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Input
                type="date"
                aria-label="First day"
                className="h-9 w-auto"
                value={period.start}
                max={period.end}
                onChange={(event) =>
                  setPeriod({ ...period, start: event.target.value })
                }
              />
              to
              <Input
                type="date"
                aria-label="Last day"
                className="h-9 w-auto"
                value={period.end}
                min={period.start}
                max={utcDay(0)}
                onChange={(event) =>
                  setPeriod({ ...period, end: event.target.value })
                }
              />
            </div>
            <Input
              aria-label="User email or ID"
              placeholder="User email or ID"
              className="h-9 w-56"
              value={userSearch}
              onChange={(event) => setUserSearch(event.target.value)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isError ? (
          <div className="rounded-md border border-destructive bg-card px-4 py-3 text-sm text-destructive">
            {error.message}
          </div>
        ) : (
          <>
            <TableLegend />
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHead
                    column="group"
                    label={grouping.label}
                    hint={grouping.hint}
                    sortBy={(row) => row.group}
                    sort={sort}
                    onSort={setSort}
                  />
                  <SortHead
                    column="outcome"
                    label="Outcome"
                    hint={OUTCOME_HINT}
                    sortBy={actedOnShare}
                    sort={sort}
                    onSort={setSort}
                  />
                  {NUMBER_COLUMNS.map(({ label, hint, sortBy }) => (
                    <SortHead
                      key={label}
                      column={label}
                      label={label}
                      hint={hint}
                      sortBy={sortBy}
                      sort={sort}
                      onSort={setSort}
                      className="text-right"
                    />
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {isLoading
                        ? 'Loading...'
                        : 'No card was shown in this period'}
                    </TableCell>
                  </TableRow>
                ) : (
                  shownRows.map((row) => {
                    const key = `${groupBy} ${String(row.group)}`;
                    const isOpen = openRow === key;
                    return (
                      <Fragment key={key}>
                        <TableRow
                          className="cursor-pointer"
                          onClick={() => setOpenRow(isOpen ? null : key)}
                        >
                          <TableCell className="font-medium">
                            {/* The click goes up to the row */}
                            <button
                              type="button"
                              aria-expanded={isOpen}
                              className="inline-flex items-center gap-1.5 text-left"
                            >
                              <ChevronRight
                                className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`}
                              />
                              {grouping.format(row.group)}
                            </button>
                          </TableCell>
                          <TableCell>
                            <OutcomeBar row={row} />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {row.impressions.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {row.cards.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {countWithShare(row.acted_on, row.cards)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {countWithShare(row.run_failed, row.cards)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {countWithShare(row.dismissed, row.cards)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {countWithShare(neither(row), row.cards)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <ActedOnWhenDecided
                              row={row}
                              shareOfAllRows={shareOfAllRows}
                              isOpen={isOpen}
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {timePerShowing(row)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {timeOpen(row)}
                          </TableCell>
                        </TableRow>
                        {isOpen ? (
                          <TableRow className="hover:bg-transparent">
                            <TableCell colSpan={COLUMN_COUNT}>
                              <RowCharts row={row} />
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};
